import { Box, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { ExternalLink, CSVDownloadButton } from '../../../components';
import { CodeBlock } from '../code-block';
import { handleCopyClick } from '../constants';
import { StyleGuidePage } from '../style-guide-page';
import { testCSVData } from '../data/csv';
import { IResponse } from '../../../models';

export const StyleGuideCSVDownloadButton = () => {
  const [isCopied, setIsCopied] = useState<{
    [val: string]: boolean;
  }>({});

  return (
    <StyleGuidePage title="CSV Download Button">
      <Grid container mb={3}>
        <Grid item xs={12} lg={8}>
          <Typography mb={1}>
            The CSV Download Button component is built upon the{' '}
            <ExternalLink
              to="https://mui.com/material-ui/react-button/#loading-button"
              target="_blank"
            >
              MUI Loading Button
            </ExternalLink>{' '}
            component. The component is intended to provide a quick, reusable way to implement CSV
            Download with column header generation, column formatting, and file creation built-in.
          </Typography>
          <Typography mb={1}>
            It is intended to pass an apiRequest, name, fileNamePrefix (aka File Name), and columns.
            Columns should have defined headers, and a key to identify the data point returned by
            the API. Additionally, you can provide options for formatting column data when defining
            columns. If returning a list of items for a single column, there is the ability to
            transform the data (for filtering, formatting, etc.) before it is joined by either a
            default or custom join and returned.
          </Typography>
          <Typography mb={1}>
            NOTE: There is a static set of types. If you need to add or edit a type, be sure to
            update this documentation and add an example.
          </Typography>
          <Grid container spacing={{ xs: 1, md: 3 }}>
            <Grid item xs={12} md={'auto'}>
              <code>
                Props:
                <br />- LoadingButtonProps
                <br />- name: string
                <br />- columns: ICSVColumn[]
                <br />- apiRequest: () =&gt; Promise&lt;any&gt;
                <br />- fileNamePrefix: string
                <br />- fileNameSuffix?: string
                <br />- transformResponse?: (response: any) =&gt; any[]
                <br />- handleData?: (data: any[]) =&gt; void
                <br />- loading?: JSX.Element | string
              </code>
            </Grid>
            <Grid item xs={12} md={'auto'}>
              <code>
                ICSVColumn Props:
                <br />- header: string
                <br />- key: string
                <br />- type?: 'date' | 'datetime' | 'currency' | 'list'
                <br />- join?: string
                <br />- transform?: (data: any[]) =&gt; void
              </code>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box mb={1}>
            <CSVDownloadButton
              name="primary-csv"
              fileNamePrefix="testPrimaryCSV"
              columns={[
                { header: 'Territory', key: 'territory' },
                { header: 'Neighborhood', key: 'neighborhoodDescription' },
                { header: 'Lot', key: 'lotNumber' },
                { header: 'Plan', key: 'plan' },
                { header: 'Total', key: 'totalPrice', type: 'currency' },
                { header: 'Created', key: 'createdDate', type: 'date' },
                { header: 'Modified', key: 'modifiedDateTime', type: 'datetime' },
                {
                  header: 'Categories',
                  key: 'categories',
                  type: 'list',
                  join: ' | ',
                  transform: (data: any[]) => {
                    return data.filter(item => item !== 'List Item 4');
                  }
                },
                { header: 'Sub-Categories', key: 'subCategories', type: 'list' }
              ]}
              apiRequest={() => testCSVData()}
              transformResponse={(res: IResponse<any[]>) => res?.records}
            >
              CSV
            </CSVDownloadButton>
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;CSVDownloadButton
            <br />
            &nbsp;&nbsp;&nbsp;name="primary-csv"
            <br />
            &nbsp;&nbsp;&nbsp;fileNamePrefix="testPrimaryCSV"
            <br />
            &nbsp;&nbsp;&nbsp;columns=&#123;[
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Territory', key: 'territory' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Neighborhood', key:
            'neighborhoodDescription' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Lot', key: 'lotNumber' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Plan', key: 'plan' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Total', key: 'totalPrice', type:
            'currency' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Created', key: 'createdDate', type:
            'date' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Modified', key: 'modifiedDateTime',
            type: 'datetime' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Categories', key: 'categories',
            type: 'list', join: ' | ', transform: (data: any[]) =&gt; &#123; return data.filter(item
            =&gt; item !== 'List Item 4'); &#125;&#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Sub-Categories', key:
            'subCategories', type: 'list' &#125;
            <br />
            &nbsp;&nbsp;&nbsp;]&#125;
            <br />
            &nbsp;&nbsp;&nbsp;apiRequest=&#123;() =&gt; testCSVData()&#125;
            <br />
            &nbsp;&nbsp;&nbsp;transformResponse=&#123;(res: IResponse&lt;any[]&gt;) =&gt;
            res?.records&#125;
            <br />
            &gt;
            <br />
            &nbsp;&nbsp;&nbsp;CSV
            <br />
            &lt;/CSVDownloadButton&gt;
          </CodeBlock>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box mb={1}>
            <CSVDownloadButton
              name="secondary-csv"
              fileNamePrefix="testSecondaryCSV"
              color="secondary"
              columns={[
                { header: 'Territory', key: 'territory' },
                { header: 'Neighborhood', key: 'neighborhoodDescription' },
                { header: 'Lot', key: 'lotNumber' },
                { header: 'Plan', key: 'plan' },
                { header: 'Total', key: 'totalPrice', type: 'currency' },
                { header: 'Created', key: 'createdDate', type: 'date' },
                { header: 'Modified', key: 'modifiedDateTime', type: 'datetime' },
                {
                  header: 'Categories',
                  key: 'categories',
                  type: 'list',
                  join: ' | ',
                  transform: (data: any[]) => {
                    return data.filter(item => item !== 'List Item 4');
                  }
                },
                { header: 'Sub-Categories', key: 'subCategories', type: 'list' }
              ]}
              apiRequest={() => testCSVData()}
              transformResponse={(res: IResponse<any[]>) => res?.records}
            >
              CSV
            </CSVDownloadButton>
          </Box>

          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;CSVDownloadButton
            <br />
            &nbsp;&nbsp;&nbsp;name="secondary-csv"
            <br />
            &nbsp;&nbsp;&nbsp;fileNamePrefix="testSecondaryCSV"
            <br />
            &nbsp;&nbsp;&nbsp;color="secondary"
            <br />
            &nbsp;&nbsp;&nbsp;columns=&#123;[
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Territory', key: 'territory' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Neighborhood', key:
            'neighborhoodDescription' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Lot', key: 'lotNumber' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Plan', key: 'plan' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Total', key: 'totalPrice', type:
            'currency' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Created', key: 'createdDate', type:
            'date' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Modified', key: 'modifiedDateTime',
            type: 'datetime' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Categories', key: 'categories',
            type: 'list', join: ' | ', transform: (data: any[]) =&gt; &#123; return data.filter(item
            =&gt; item !== 'List Item 4'); &#125;&#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Sub-Categories', key:
            'subCategories', type: 'list' &#125;
            <br />
            &nbsp;&nbsp;&nbsp;]&#125;
            <br />
            &nbsp;&nbsp;&nbsp;apiRequest=&#123;() =&gt; testCSVData()&#125;
            <br />
            &nbsp;&nbsp;&nbsp;transformResponse=&#123;(res: IResponse&lt;any[]&gt;) =&gt;
            res?.records&#125;
            <br />
            &gt;
            <br />
            &nbsp;&nbsp;&nbsp;CSV
            <br />
            &lt;/CSVDownloadButton&gt;
          </CodeBlock>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box mb={1}>
            <CSVDownloadButton
              name="inherit-csv"
              fileNamePrefix="testInheritCSV"
              color="inherit"
              columns={[
                { header: 'Territory', key: 'territory' },
                { header: 'Neighborhood', key: 'neighborhoodDescription' },
                { header: 'Lot', key: 'lotNumber' },
                { header: 'Plan', key: 'plan' },
                { header: 'Total', key: 'totalPrice', type: 'currency' },
                { header: 'Created', key: 'createdDate', type: 'date' },
                { header: 'Modified', key: 'modifiedDateTime', type: 'datetime' },
                {
                  header: 'Categories',
                  key: 'categories',
                  type: 'list',
                  join: ' | ',
                  transform: (data: any[]) => {
                    return data.filter(item => item !== 'List Item 4');
                  }
                },
                { header: 'Sub-Categories', key: 'subCategories', type: 'list' }
              ]}
              apiRequest={() => testCSVData()}
              transformResponse={(res: IResponse<any[]>) => res?.records}
            >
              CSV
            </CSVDownloadButton>
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;CSVDownloadButton
            <br />
            &nbsp;&nbsp;&nbsp;name="inherit-csv"
            <br />
            &nbsp;&nbsp;&nbsp;fileNamePrefix="testInheritCSV"
            <br />
            &nbsp;&nbsp;&nbsp;color="inherit"
            <br />
            &nbsp;&nbsp;&nbsp;columns=&#123;[
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Territory', key: 'territory' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Neighborhood', key:
            'neighborhoodDescription' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Lot', key: 'lotNumber' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Plan', key: 'plan' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Total', key: 'totalPrice', type:
            'currency' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Created', key: 'createdDate', type:
            'date' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Modified', key: 'modifiedDateTime',
            type: 'datetime' &#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Categories', key: 'categories',
            type: 'list', join: ' | ', transform: (data: any[]) =&gt; &#123; return data.filter(item
            =&gt; item !== 'List Item 4'); &#125;&#125;,
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123; header: 'Sub-Categories', key:
            'subCategories', type: 'list' &#125;
            <br />
            &nbsp;&nbsp;&nbsp;]&#125;
            <br />
            &nbsp;&nbsp;&nbsp;apiRequest=&#123;() =&gt; testCSVData()&#125;
            <br />
            &nbsp;&nbsp;&nbsp;transformResponse=&#123;(res: IResponse&lt;any[]&gt;) =&gt;
            res?.records&#125;
            <br />
            &gt;
            <br />
            &nbsp;&nbsp;&nbsp;CSV
            <br />
            &lt;/CSVDownloadButton&gt;
          </CodeBlock>
        </Grid>
      </Grid>
    </StyleGuidePage>
  );
};
